import { useState, useEffect } from 'react';
import { IconChevronDownFilled } from '@tabler/icons-react';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';

const inputClass =
  'w-full outline-none bg-white/[0.08] focus:bg-white/[0.12] focus:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] placeholder:text-white/30 transition-all duration-200 ease-out';

const inputStyle = {
  border: 'none',
  borderRadius: '16px',
  height: '48px',
  padding: '0 16px',
  fontSize: '15px',
  fontWeight: 500,
  fontFamily: FONT,
  color: 'rgba(255, 255, 255, 0.95)',
  width: '100%',
  boxSizing: 'border-box',
};

const selectStyle = {
  ...inputStyle,
  appearance: 'none',
  WebkitAppearance: 'none',
  cursor: 'pointer',
  paddingRight: '48px',
};

const textareaStyle = {
  ...inputStyle,
  borderRadius: '18px',
  height: 'auto',
  minHeight: '96px',
  padding: '14px 16px',
  resize: 'none',
  lineHeight: '21px',
};

const labelStyle = {
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.45)',
  fontFamily: FONT,
};

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'debit', label: 'Débito' },
  { value: 'credit', label: 'Crédito' },
  { value: 'mixed', label: 'Mixto' },
  { value: 'pending', label: 'Pendiente' },
];

function toLocalDate(value) {
  if (!value) return '';
  const dt = new Date(value);
  if (isNaN(dt.getTime())) return '';
  const offset = dt.getTimezoneOffset();
  return new Date(dt.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function SelectChevron() {
  return (
    <span
      className="flex items-center justify-center pointer-events-none absolute w-6 h-6 rounded-full"
      style={{
        right: '10px',
        top: '50%',
        transform: 'translateY(-50%)',
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
      }}
    >
      <IconChevronDownFilled size={12} color="rgba(255, 255, 255, 0.55)" />
    </span>
  );
}

export default function ModalEditarGasto({ isOpen, onClose, onSaved, notify, expense }) {
  const [form, setForm] = useState({
    descripcion: '',
    monto: '',
    categoria: '',
    fecha: '',
    metodo: 'cash',
    proveedor: '',
    notas: '',
  });
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && expense) {
      setForm({
        descripcion: expense.description || '',
        monto: expense.amount != null ? String(expense.amount) : '',
        categoria: expense.category_id != null ? String(expense.category_id) : '',
        fecha: toLocalDate(expense.expense_date),
        metodo: expense.payment_method || 'cash',
        proveedor: expense.supplier_vendor || '',
        notas: expense.notes || '',
      });
      setSaving(false);
      fetchCategories();
    }
  }, [isOpen, expense]);

  const fetchCategories = async () => {
    try {
      const res = await apiFetch(`/expense-categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data);
      }
    } catch {}
  };

  if (!isOpen || !expense) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const montoNum = parseFloat(form.monto);
  const canSave = form.descripcion.trim() && !isNaN(montoNum) && montoNum > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;

    setSaving(true);
    notify?.('loading', 'Actualizando gasto...');
    const startedAt = Date.now();

    try {
      const body = {};
      if (form.descripcion.trim() !== (expense.description || '')) body.description = form.descripcion.trim();
      if (montoNum !== (expense.amount || 0)) body.amount = montoNum;
      if (form.fecha) {
        const newDate = new Date(`${form.fecha}T12:00:00`).toISOString();
        if (newDate !== expense.expense_date) body.expense_date = newDate;
      }
      if (form.metodo !== (expense.payment_method || 'cash')) body.payment_method = form.metodo;
      const catVal = form.categoria ? Number(form.categoria) : null;
      if (catVal !== (expense.category_id || null)) body.category_id = catVal;
      if (form.proveedor.trim() !== (expense.supplier_vendor || '')) body.supplier_vendor = form.proveedor.trim() || null;
      if (form.notas.trim() !== (expense.notes || '')) body.notes = form.notas.trim() || null;

      if (Object.keys(body).length === 0) {
        notify?.('clear');
        notify?.('success', 'No hay cambios para guardar');
        onClose();
        return;
      }

      const res = await apiFetch(`/expenses/${expense.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let msg = 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.';
        try {
          const err = await res.json();
          if (err.detail) msg = err.detail;
          else if (Array.isArray(err.detail)) msg = err.detail.map((d) => d.msg).join(', ');
        } catch {}
        notify?.('clear');
        notify?.('error', msg);
        return;
      }

      await ensureMinLoading(startedAt);
      notify?.('clear');
      notify?.('success', 'Gasto actualizado correctamente');
      onSaved?.();
      onClose();
    } catch {
      notify?.('clear');
      notify?.('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.72)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={saving ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="editar-gasto-titulo"
        className="flex flex-col overflow-hidden w-full"
        style={{
          backgroundColor: '#000',
          borderRadius: '28px',
          border: '1px solid rgba(255, 255, 255, 0.16)',
          maxWidth: '880px',
          maxHeight: 'min(760px, calc(100dvh - 24px))',
          boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-white/[0.16]">
          <h2
            id="editar-gasto-titulo"
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Editar gasto
          </h2>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Modifica los datos del gasto seleccionado
          </p>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-5 sm:px-6 py-5">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Columna izquierda */}
            <div className="flex-1 flex flex-col gap-5 min-w-0">
              <div className="flex flex-col gap-2">
                <label htmlFor="editar-gasto-descripcion" style={labelStyle}>
                  Descripción *
                </label>
                <input
                  id="editar-gasto-descripcion"
                  type="text"
                  value={form.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  placeholder="Ej. Compra de harina"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-gasto-categoria" style={labelStyle}>
                  Categoría
                </label>
                <div className="relative">
                  <select
                    id="editar-gasto-categoria"
                    value={form.categoria}
                    onChange={(e) => handleChange('categoria', e.target.value)}
                    className={inputClass}
                    style={selectStyle}
                  >
                    <option value="" style={{ backgroundColor: '#1a1a1a', color: '#fff' }}>
                      Sin categoría
                    </option>
                    {categories.map((c) => (
                      <option
                        key={c.id}
                        value={c.id}
                        style={{ backgroundColor: '#1a1a1a', color: '#fff' }}
                      >
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <SelectChevron />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-gasto-metodo" style={labelStyle}>
                  Método de pago
                </label>
                <div className="relative">
                  <select
                    id="editar-gasto-metodo"
                    value={form.metodo}
                    onChange={(e) => handleChange('metodo', e.target.value)}
                    className={inputClass}
                    style={selectStyle}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option
                        key={m.value}
                        value={m.value}
                        style={{ backgroundColor: '#1a1a1a', color: '#fff' }}
                      >
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <SelectChevron />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-gasto-notas" style={labelStyle}>
                  Notas
                </label>
                <textarea
                  id="editar-gasto-notas"
                  value={form.notas}
                  onChange={(e) => handleChange('notas', e.target.value)}
                  placeholder="Opcional"
                  rows={3}
                  className={`${inputClass} resize-none`}
                  style={textareaStyle}
                />
              </div>
            </div>

            {/* Columna derecha */}
            <div className="flex-1 flex flex-col gap-5 min-w-0">
              <div className="flex flex-col gap-2">
                <label htmlFor="editar-gasto-monto" style={labelStyle}>
                  Monto (MXN) *
                </label>
                <input
                  id="editar-gasto-monto"
                  type="number"
                  min="0.01"
                  step="0.01"
                  inputMode="decimal"
                  value={form.monto}
                  onChange={(e) => handleChange('monto', e.target.value)}
                  placeholder="0.00"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-gasto-fecha" style={labelStyle}>
                  Fecha del gasto
                </label>
                <input
                  id="editar-gasto-fecha"
                  type="date"
                  value={form.fecha}
                  onChange={(e) => handleChange('fecha', e.target.value)}
                  className={inputClass}
                  style={{ ...inputStyle, colorScheme: 'dark' }}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="editar-gasto-proveedor" style={labelStyle}>
                  Proveedor o proveedora
                </label>
                <input
                  id="editar-gasto-proveedor"
                  type="text"
                  value={form.proveedor}
                  onChange={(e) => handleChange('proveedor', e.target.value)}
                  placeholder="Opcional"
                  className={inputClass}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-2 shrink-0 px-5 sm:px-6 py-4 border-t border-white/[0.16]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.85]"
            style={{
              backgroundColor: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: '#000',
              fontFamily: FONT,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center justify-center h-12 px-7 rounded-full border-none touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
            style={{
              backgroundColor: canSave ? '#0071e3' : 'rgba(255, 255, 255, 0.06)',
              fontSize: '15px',
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: canSave ? '#fff' : 'rgba(255, 255, 255, 0.35)',
              fontFamily: FONT,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}
